import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Activity } from "lucide-react";
import QuickPanel from "@/components/monitoring/QuickPanel";
import GoalsPanel from "@/components/monitoring/GoalsPanel";
import DirezioneDashboard from "@/components/monitoring/DirezioneDashboard";
import RegistryPanel from "@/components/monitoring/RegistryPanel";

export default function MonitoraggioPage() {
  const { user } = useAuth();
  const [ctx, setCtx] = useState(null);
  const [offices, setOffices] = useState([]);
  const [officeId, setOfficeId] = useState("");
  const cross = user?.role === "admin" || user?.role === "backoffice";
  const isAdmin = user?.role === "admin";

  const seq = useRef(0);
  const [identityError, setIdentityError] = useState(false);
  const load = useCallback(async () => {
    const n = ++seq.current;
    try {
      const params = { _ts: Date.now(), ...(cross && officeId ? { office_id: officeId } : {}) };
      let { data } = await api.get("/monitoring/context", { params });
      if (data.operator_id !== user.id) {
        ({ data } = await api.get("/monitoring/context", { params: { ...params, _ts: Date.now() + 1 } }));
      }
      if (n !== seq.current) return;
      const wrongOffice = !cross && user.office_id && data.office_id !== user.office_id;
      if (data.operator_id !== user.id || wrongOffice) { setIdentityError(true); setCtx(null); return; }
      setIdentityError(false);
      setCtx(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore caricamento"); }
  }, [officeId, cross, user]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (cross) api.get("/offices").then(({ data }) => setOffices(data.filter((o) => o.active !== false))).catch(() => {}); }, [cross]);

  return (
    <div className="space-y-4" data-testid="monitoraggio-page">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-semibold text-slate-800"><Activity className="h-5 w-5 text-brand" /> Monitoraggio Sedi</h1>
          <p className="text-xs text-slate-500">Ingressi, contratti, obiettivi e badge · {ctx ? new Date(ctx.date + "T00:00:00").toLocaleDateString("it-IT", { weekday: "long", day: "2-digit", month: "long", year: "numeric" }) : ""}</p>
        </div>
        {cross && (
          <select className="h-9 rounded-md border border-border bg-white px-2 text-sm" value={officeId} onChange={(e) => setOfficeId(e.target.value)} data-testid="mon-office-select">
            <option value="">Tutte le sedi</option>
            {offices.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        )}
      </div>
      {identityError && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-danger" data-testid="mon-identity-error">
          I dati ricevuti non corrispondono al tuo account o alla tua sede: per sicurezza non vengono mostrati. Ricarica la pagina; se il problema persiste contatta la Direzione.
        </div>
      )}
      <Tabs defaultValue={cross ? "dashboard" : "registra"}>
        <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1 bg-slate-50 p-1" data-testid="mon-tabs">
          {cross && <TabsTrigger value="dashboard" data-testid="mon-tab-dashboard">Dashboard</TabsTrigger>}
          <TabsTrigger value="registra" data-testid="mon-tab-registra">{cross ? "Registrazione" : "Monitoraggio giornaliero"}</TabsTrigger>
          <TabsTrigger value="badge" data-testid="mon-tab-badge">{isAdmin ? "Obiettivi e Badge" : "Badge sede"}</TabsTrigger>
          <TabsTrigger value="storico" data-testid="mon-tab-storico">{cross ? "Verifica registrazioni" : "Storico"}</TabsTrigger>
        </TabsList>
        {cross && <TabsContent value="dashboard"><DirezioneDashboard officeId={officeId} isAdmin={isAdmin} /></TabsContent>}
        <TabsContent value="registra">{identityError ? null : <QuickPanel ctx={ctx} officeId={officeId} cross={cross} onChanged={load} />}</TabsContent>
        <TabsContent value="badge"><GoalsPanel officeId={officeId || ctx?.office_id} isAdmin={isAdmin} offices={offices} /></TabsContent>
        <TabsContent value="storico"><RegistryPanel officeId={officeId} cross={cross} onChanged={load} /></TabsContent>
      </Tabs>
    </div>
  );
}
